import { faMobile } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  CardHeader,
  DialogContent,
  FormHelperText,
  Grid,
  Link,
} from '@mui/material';
import AuthenticatorContext from 'components/Authenticator/AuthenticatorContext';
import { MobileFriendlyMuiOtpInput } from 'modules/Profile/styles';
import {
  useCallback,
  useContext,
} from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Title } from 'styles/styles';
import { useLocation } from 'wouter';

import AuthForm from './AuthForm';

interface TotpFormData {
  code: string;
}

export default function Totp() {
  const formControl = useForm<TotpFormData>({ mode: 'onTouched' });
  const [, setLocation] = useLocation();
  const {
    handleSubmit,
  } = formControl;
  const { loginTotp, performingLogin, error } = useContext(AuthenticatorContext);

  const onSubmit: SubmitHandler<TotpFormData> = useCallback(async ({ code }) => {
    await loginTotp(code);
  }, [loginTotp]);

  const handleToRecovery = useCallback(() => {
    setLocation('/recovery');
  }, [setLocation]);

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...formControl}>
        <AuthForm>
          <DialogContent>
            <Grid container spacing={2} direction="column">
              <Grid item display="flex" justifyContent="center">
                <FontAwesomeIcon icon={faMobile} size="6x" />
              </Grid>
              <Grid item display="flex" justifyContent="center">
                <CardHeader
                  title={(
                    <Title>
                      <FormattedMessage id="mfa_login_title" />
                    </Title>
                  )}
                  subheader={(
                    <FormattedMessage id="mfa_login_subheader" />
                  )}
                />
              </Grid>
              <Grid item display="flex" justifyContent="center">
                <Controller
                  name="code"
                  control={formControl.control}
                  rules={{ validate: (value) => value?.length === 6 }}
                  render={({ field, fieldState }) => (
                    <Box width="75%" justifyContent="center" display="flex" flexDirection="column">
                      <Grid container justifyContent="center">
                        <MobileFriendlyMuiOtpInput
                          {...field}
                          sx={{ gap: 1 }}
                          length={6}
                        />
                      </Grid>
                      {fieldState.invalid ? (
                        <FormHelperText error>
                          <FormattedMessage id="mfa_code_invalid" />
                        </FormHelperText>
                      ) : null}
                      {error && (
                        <Alert severity="error" sx={{ marginTop: 2 }}>
                          <FormattedMessage id="mfa_login_code_error" />
                        </Alert>
                      )}
                    </Box>
                  )}
                />
              </Grid>
              <Grid item display="flex" justifyContent="center" padding={2}>
                <LoadingButton loading={performingLogin} type="submit" variant="contained" color="secondary" size="large" sx={{ width: '80%' }}>
                  <FormattedMessage id="mfa_verify" />
                </LoadingButton>
              </Grid>
              <Grid item display="flex" justifyContent="center" padding={2}>
                <Button
                  type="button"
                  component={Link}
                  onClick={handleToRecovery}
                  sx={{ textTransform: 'none' }}
                >
                  <FormattedMessage id="mfa_login_to_recovery" />
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </AuthForm>
      </FormProvider>
    </form>
  );
}
